/**
 * src/lib/voice/normalizeBreadcrumbNote.js — TICKET-023 defensive normalization.
 *
 * The `voice_records` table can transiently land in an inconsistent state
 * where `status='ready'` but the joined `transcripts` array is empty. This
 * happens when the server-side capture handler sets the row to READY before
 * the voice_transcripts row is committed (race), or when an RLS policy
 * filters voice_transcripts but not voice_records (visibility mismatch).
 *
 * Symptom on the user side (CEO live test 2026-04-26 22:38 on iPhone): the
 * Breadcrumbs section shows a card that looks like the recording was lost
 * — empty body, no transcript text. CEO reported "we have lost breadcrumbs".
 *
 * The data isn't actually lost (the voice_records row exists, the audio is
 * stored, the transcript may exist on the server but not yet visible), it's
 * just NOT READY YET. Showing it as 'transcribing' instead of 'ready' fixes
 * the UX bug — the user sees a "still processing" state instead of a blank
 * breadcrumb.
 *
 * This is a CLIENT-SIDE defensive layer; the underlying server-side
 * inconsistency should also be fixed (separate ticket — see TICKET-023
 * Notes for the data-path investigation).
 */

/**
 * @param {Object} note  voice_records row as returned by /api/ai voice.list
 * @returns {Object}     same note, possibly with status downgraded
 */
export function normalizeBreadcrumbNote(note) {
  if (!note || typeof note !== 'object') return note;
  // Only intervene when the inconsistency is unambiguous: status says READY
  // but the transcripts array is missing or empty. Other states pass through.
  if (note.status !== 'ready') return note;
  const hasTranscripts = Array.isArray(note.transcripts) && note.transcripts.length > 0;
  if (hasTranscripts) return note;
  return {
    ...note,
    status: 'transcribing',
    _normalized_reason: 'ready_without_transcripts',
  };
}

export function normalizeBreadcrumbNotes(notes) {
  if (!Array.isArray(notes)) return [];
  return notes.map(normalizeBreadcrumbNote);
}
